import React, { useMemo, useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { ThreeCircles } from "react-loader-spinner";
import * as Icon from "react-bootstrap-icons";
import { ColDef } from "ag-grid-community";
import styles from "./FieldMappings.module.css";
import CustomButton from "../../reusable_components/custom_button_component/Button";
import ActionsRenderer from "./components/ActionsRenderer/ActionsRenderer";
import CygnusGrid from "../../../pages/reusable_components/CygnusGrid/CygnusGrid";
import DropdownOptionsModal from "./components/DropdownOptionsModal/DropdownOptionsModal";
import {
  getAllFields,
} from "../../client_onboarding/MDMFieldsConfig/MDMFieldsConfig.service";

/**
 * Renders the approval details page, showing engagement letter approval data and actions.
 *
 * This component includes:
 * - Display of approval details in a grid.
 * - Action buttons for approving or rejecting engagement letters.
 * - Modals for template preview and message confirmation.
 * - Loader to indicate grid data refresh.
 *
 * @component
 * @returns {JSX.Element} The rendered approval details page with a grid, action buttons, and modals
 */
const FieldMappingsPage: React.FC = () => {
  const [refreshGrid, setRefreshGrid] = useState<boolean>(false);
  const [fieldMappings, setFieldMappings] = useState<any[]>([
    {
      FieldName: "Niche",
      Description: "Specifies the primary category or domain the client belongs to."
    },
    {
      FieldName: "Sub-Niche",
      Description: "Defines a more detailed specialization within the selected niche."
    }
  ]);
  const [mappingModal, setMappingModal] = useState<boolean>(false);
  const [key, setKey] = useState<number | undefined>(undefined);
  const [selectedFieldsList, setSelectedFieldsList] = useState<any[]>([]);

  // Style for the AgGrid
  const gridStyle = useMemo(() => ({ height: "85vh", width: "100%" }), []);

  // Default column definitions for the grid
  const defaultApprovalDetailsColumnDefs: ColDef[] = [
    {
      headerName: "Field Name",
      field: "FieldName",
      sortable: false,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      flex: 1,
    },
    {
      headerName: "Description",
      field: "Description",
      sortable: false,
      flex: 1,
    },
    {
      headerName: "Action",
      pinned: "right",
      maxWidth: 130,
      minWidth: 130,
      cellRenderer: (params: any) => (
        <ActionsRenderer
          onEdit={() => setMappingModal(true)}
        />
      ),
      flex: 1,
      sortable: false
    }
  ];

  const [fieldMappingsColumnDefs, setApprovalDetailsColumnDefs] = useState(defaultApprovalDetailsColumnDefs);

  useEffect(() => {
    const init = async () => {
      const allFieldData = await getAllFields();
      const selectedFieldData = allFieldData.map(item => ({
        id: parseInt(item.id, 10),
        label: item.field_name,
        value: item.field_code
      }));
      setSelectedFieldsList(selectedFieldData);
    };

    init();
  }, []);

  // JSX for the page component
  return (
    <div className={styles.fieldMappingsPage}>
      {mappingModal && (
        <DropdownOptionsModal
          show={true}
          selectedFieldsList={selectedFieldsList}
          onHide={() => setMappingModal(false)}
        />
      )}
      <Row className='no-gutter'>
        <p className={`display-6 ${styles.fieldMappingsHeading}`}>
          Mappings
        </p>
      </Row>
      <Row className='no-gutter'>
        <Col md={12} className={`d-flex justify-content-end ${styles.actionButtonsContainer}`}>
          <div className={styles.actionButton}>
            <CustomButton
              onclick={() => setMappingModal(true)}
              content={"Create Mapping"}
            />
          </div>
          <div className={styles.refreshIconWrapper}>
            {
              refreshGrid ?
                <div>
                  <ThreeCircles
                    height="20"
                    width="20"
                    color="#4fa94d"
                    wrapperStyle={{}}
                    wrapperClass=""
                    visible={true}
                    ariaLabel="three-circles-rotating"
                    outerCircleColor="var(--theme1_tertiary)"
                    innerCircleColor="var(--theme1_secondary)"
                    middleCircleColor="var(--theme1_primary)"
                  />
                </div>
                :
                <Icon.ArrowCounterclockwise
                  onClick={() => { }}
                  className={styles.refreshIcon}
                />
            }
          </div>
        </Col>
      </Row>
      <Row className='no-gutter'>
        <Col md={12}>
          <div className="ag-theme-balham" style={gridStyle}>
            <CygnusGrid
              key={key}
              fetchData={async () => {
                // Since this is not paginated, just return everything
                return { records: fieldMappings, totalCount: fieldMappings.length };
              }}
              rowModelType="clientSide"
              dataIdentifier="FirmReferenceDataId"
              cachedData={fieldMappings}
              setCachedData={setFieldMappings}
              columnDefs={fieldMappingsColumnDefs}
              rowSelection={"multiple"}
              cachedIsLoading={refreshGrid}
              onLoadingChange={setRefreshGrid}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default FieldMappingsPage;